import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["as", "iconSize", "size", "semanticColor", "color", "title", "titleId", "desc", "descId", "autoMirror", "width", "height", "aria-label", "aria-labelledby", "aria-describedby", "className", "style"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** This component has been marked as a server component in /scripts/use-client..mjs **/

import React from 'react';
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { cssColorValue } from "../../styles/semantic-theme.mjs";
import { cssGraphicSize } from "../../styles/index.mjs";
import { ecx } from "../../utils.mjs";
// If this updates, ProgressCircle needs to be updated as well
export var IconSizeMap = {
  xsmall: 12,
  small: 16,
  medium: 24,
  large: 32,
  xlarge: 48,
  xxlarge: 64,
  xxxlarge: 64,
  xxxxlarge: 88
};
var getIconSizeKey = function getIconSizeKey(iconSize) {
  return Object.keys(IconSizeMap).find(function (key) {
    return IconSizeMap[key] === iconSize;
  });
};

// return the highest possible size that is <= the requested size,
// we sort asc to return the smallest size if requested value < smallest size
export var findClosestGlyphAvailable = function findClosestGlyphAvailable(glyphList, val) {
  return glyphList.sort(function (a, b) {
    return a.size - b.size;
  }).reduce(function (prev, current) {
    if (current.size <= val && current.size > prev.size) {
      return current;
    }
    return prev;
  });
};
function getIconPurpose(attrs) {
  var hasAssistiveText = Object.values(attrs).some(function (attr) {
    return attr;
  });
  return hasAssistiveText ? 'informative' : 'decorative';
}
// If this updates, ProgressCircle needs to be updated as well
var graphicSizeMap = {
  xsmall: 'smaller-2',
  small: 'smaller',
  medium: 'base',
  large: 'larger',
  xlarge: 'larger-3',
  xxlarge: 'larger-4',
  xxxlarge: 'larger-4',
  xxxxlarge: 'larger-5'
};

/* Now that width and height are defined in css
   we need to listen for width and height attributes
   and apply them, with units, when they are present
*/
var attrToStyle = function attrToStyle(attr) {
  // check if attr already has units
  var hasUnits = isNaN(Number(attr));
  // append px only if needed
  return hasUnits ? attr : "".concat(attr, "px");
};
var getDimensionProp = function getDimensionProp(tokenType, mergedSize, rawSize) {
  /* if 'informative' or not medium size, update size via inline styles
      otherwise, we can fall back to default css
  */
  if (tokenType === 'informative' || mergedSize !== 'medium') {
    return cssGraphicSize(graphicSizeMap[mergedSize], tokenType);
  }
  if (rawSize) {
    return attrToStyle(rawSize);
  }
  return undefined;
};

/**
 * Icon is a component that renders an SVG icon. The icon can be styled with a semantic color, and can be sized using the `size` prop.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/story/components-icon--all-icons) |
 * [Encore site](https://encore.spotify.net/components/icon) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/Icon) |
 * [Figma](https://www.figma.com/design/UClGFW4QNu6wbv7imzhLTG/%F0%9F%92%9A-Encore-Foundations?m=auto&node-id=2-2&t=E6ePOly5vsCF7JYU-1)
 *
 */
export var Icon = function Icon(_ref) {
  var as = _ref.as,
    iconSize = _ref.iconSize,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    semanticColor = _ref.semanticColor,
    color = _ref.color,
    title = _ref.title,
    titleId = _ref.titleId,
    desc = _ref.desc,
    descId = _ref.descId,
    autoMirror = _ref.autoMirror,
    width = _ref.width,
    height = _ref.height,
    ariaLabel = _ref['aria-label'],
    ariaLabelledBy = _ref['aria-labelledby'],
    ariaDescribedBy = _ref['aria-describedby'],
    className = _ref.className,
    style = _ref.style,
    props = _objectWithoutProperties(_ref, _excluded);
  var mergedSize = iconSize ? getIconSizeKey(iconSize) : size;
  var tokenType = getIconPurpose({
    ariaLabel: ariaLabel,
    ariaLabelledBy: ariaLabelledBy,
    ariaDescribedBy: ariaDescribedBy,
    title: title,
    desc: desc
  });
  var iconColorProp = semanticColor ? cssColorValue(semanticColor) : undefined;
  var heightProp = getDimensionProp(tokenType, mergedSize, height);
  var widthProp = getDimensionProp(tokenType, mergedSize, width);
  var Element = as ? as : 'svg';
  return /*#__PURE__*/React.createElement(Element, Object.assign({
    "data-encore-id": ENCORE_DATA_IDS.Icon,
    role: "img",
    "aria-label": ariaLabel,
    "aria-labelledby": title && titleId ? titleId : ariaLabelledBy,
    "aria-describedby": desc && descId ? descId : ariaDescribedBy,
    "aria-hidden": tokenType === 'decorative',
    className: ecx(['icon', 'baseline', {
      'icon--auto-mirror': autoMirror
    }], className),
    style: _objectSpread({
      '--encore-icon-fill': iconColorProp,
      '--encore-icon-height': heightProp,
      '--encore-icon-width': widthProp
    }, style)
  }, props));
};